import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import ConfirmDialog from "../components/ConfirmDialog";
import InvitationRoster from "../components/users/InvitationRoster";
import InviteMemberDialog from "../components/users/InviteMemberDialog";
import MemberDeactivateDialog from "../components/users/MemberDeactivateDialog";
import MemberOwnershipDialog from "../components/users/MemberOwnershipDialog";
import MemberRoleDialog from "../components/users/MemberRoleDialog";
import MemberRoster from "../components/users/MemberRoster";
import TeamFormDialog from "../components/users/TeamFormDialog";
import TeamManagerDialog from "../components/users/TeamManagerDialog";
import TeamMemberDialog from "../components/users/TeamMemberDialog";
import TeamRestoreDialog from "../components/users/TeamRestoreDialog";
import TeamsPanel from "../components/users/TeamsPanel";
import UserSeatSummary from "../components/users/UserSeatSummary";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { reportError } from "../lib/errorReporter";
import {
  REACTIVATION_SUMMARY,
  apiCode,
  apiFieldErrors,
  apiMessage,
  cooldownText,
  invitableRoles,
  newRequestId,
  retryAfterSeconds,
} from "../lib/memberManagement";
import { moveRequest, ownTeam, restoreRequest } from "../lib/teamManagement";

const GENERIC = "Възникна неочаквана грешка. Опитайте отново по-късно.";
const FORBIDDEN = "Нямате право да управлявате потребителите на организацията.";
// The ONE lifecycle action that turns the acting person into a Търговец, after
// which /organization/teams is refused for them. Named once, used in both places
// that have to know about it.
const OWNERSHIP_ACTION = "transfer-ownership";

// The three confirmations of the team surface, in one place. Each one states what
// does NOT move, because that is the question an Owner actually has. Изтриването is
// the only irreversible one, and says so.
const TEAM_CONFIRM_COPY = {
  remove: {
    title: "Премахване от екип",
    body: "Търговецът остава потребител в организацията със същата роля и същите оферти. Само членството в екипа се прекратява.",
    confirm: "Премахни",
  },
  archive: {
    title: "Архивиране на екип",
    body: "Екипът ще стане архивиран и няма да се показва при разпределяне. Потребителите и офертите остават непроменени.",
    confirm: "Архивирай",
  },
  delete: {
    title: "Изтриване на екип",
    body: "Екипът ще бъде премахнат от организацията и няма да може да бъде възстановен. Историята се запазва за одит, а името се освобождава за нов екип. Потребителите, ролите и офертите остават непроменени.",
    confirm: "Изтрий",
  },
};

/**
 * Потребители — the tenant user-management page.
 *
 * WHO SEES IT: an Owner and a Manager. A Sales user is shown the Bulgarian
 * forbidden card, and the backend refuses every one of these endpoints for them
 * anyway - the check here only avoids rendering a wall of errors.
 *
 * WHAT IS AUTHORITATIVE: everything. The member roster, the invitation roster,
 * the seat numbers, who may revoke, who may resend, how long the cooldown has
 * left, whether a member may be deactivated, whether their offers must be handed
 * over first and whether a role change fits the licence all come from the
 * database. This page holds no role logic and no seat arithmetic, so a mistake in
 * this file cannot widen a permission or invent a free seat.
 *
 * THE LIFECYCLE ACTIONS are offered to an OWNER only, and never on the Owner row:
 * ownership transfer is a separate operation and is not promised here.
 */
export default function OrganizationUsers() {
  const { currentOrg, refreshOrgs } = useAuth();
  const role = currentOrg?.role || "";
  const allowed = role === "owner" || role === "manager";

  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState("");

  const [invitations, setInvitations] = useState([]);
  const [seats, setSeats] = useState({});
  const [permissions, setPermissions] = useState({});
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [invitationsError, setInvitationsError] = useState("");
  const [filter, setFilter] = useState("all");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePrefill, setInvitePrefill] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteFieldErrors, setInviteFieldErrors] = useState(null);

  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState("");

  const [rowBusyId, setRowBusyId] = useState("");
  const [notice, setNotice] = useState(null);

  // Phase 2D.3A — the team structure. An Owner receives every team plus the
  // eligible Manager/Sales candidates; a Manager receives ONLY the team they
  // manage and no candidates, so this state can never offer them a control the
  // server would refuse.
  const [teams, setTeams] = useState([]);
  const [teamCandidates, setTeamCandidates] = useState({ managers: [], sales: [] });
  const [teamPermissions, setTeamPermissions] = useState({});
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState("");
  const [teamDialog, setTeamDialog] = useState(null); // { mode, team }
  const [teamBusy, setTeamBusy] = useState(false);
  const [teamDialogError, setTeamDialogError] = useState("");
  const [teamConfirm, setTeamConfirm] = useState(null); // { mode, team, member }

  // Phase 2D.2 checkpoint 3 — one lifecycle dialog at a time, always driven by a
  // freshly loaded impact. The impact is never cached: seats and open offers move
  // while the page is open.
  const [lifecycle, setLifecycle] = useState(null); // { mode, member }
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [lifecycleError, setLifecycleError] = useState("");
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  // ONE resend key per invitation per user action, so an automatic retry of the
  // same click is recognised by the database as a replay: no second rotation, no
  // second e-mail, no second audit row.
  const resendKeys = useRef({});

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    setMembersError("");
    try {
      const r = await api.get("/organization/members/roster");
      setMembers(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (err) {
      reportError("users.members");
      setMembersError(apiMessage(err, GENERIC));
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const loadInvitations = useCallback(
    async (status) => {
      setInvitationsLoading(true);
      setInvitationsError("");
      try {
        const r = await api.get("/organization/invitations", {
          params: { status: status === "all" ? undefined : status, limit: 200 },
        });
        setInvitations(Array.isArray(r.data?.items) ? r.data.items : []);
        setSeats(r.data?.seats || {});
        setPermissions(r.data?.permissions || {});
      } catch (err) {
        reportError("users.invitations");
        setInvitationsError(apiMessage(err, GENERIC));
        setInvitations([]);
      } finally {
        setInvitationsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!allowed) return;
    loadMembers();
  }, [allowed, loadMembers]);

  const loadTeams = useCallback(async () => {
    setTeamsLoading(true);
    setTeamsError("");
    try {
      const r = await api.get("/organization/teams");
      setTeams(Array.isArray(r.data?.teams) ? r.data.teams : []);
      setTeamCandidates({
        managers: Array.isArray(r.data?.eligible_managers) ? r.data.eligible_managers : [],
        sales: Array.isArray(r.data?.eligible_sales) ? r.data.eligible_sales : [],
      });
      setTeamPermissions(r.data?.permissions || {});
    } catch (err) {
      reportError("users.teams");
      setTeamsError(apiMessage(err, GENERIC));
      setTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    loadTeams();
  }, [allowed, loadTeams]);

  useEffect(() => {
    if (!allowed) return;
    loadInvitations(filter);
  }, [allowed, filter, loadInvitations]);

  const roles = useMemo(() => invitableRoles(permissions, seats), [permissions, seats]);

  const openInvite = (prefill = null) => {
    setInvitePrefill(prefill);
    setInviteError("");
    setInviteFieldErrors(null);
    setInviteOpen(true);
  };

  const submitInvite = async (payload) => {
    setInviteBusy(true);
    setInviteError("");
    setInviteFieldErrors(null);
    try {
      const r = await api.post("/organization/invitations", payload);
      setInviteOpen(false);
      setInvitePrefill(null);
      const delivery = r.data?.delivery || {};
      setNotice({
        tone: delivery.delivered === false && !delivery.skipped ? "warn" : "ok",
        text: delivery.message || "Поканата е създадена успешно.",
      });
      await Promise.all([loadInvitations(filter), loadMembers()]);
    } catch (err) {
      reportError("users.invite");
      setInviteFieldErrors(apiFieldErrors(err));
      setInviteError(apiMessage(err, GENERIC));
    } finally {
      setInviteBusy(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeBusy(true);
    setRevokeError("");
    try {
      await api.post(`/organization/invitations/${revokeTarget.id}/revoke`);
      setRevokeTarget(null);
      setNotice({ tone: "ok", text: "Поканата е отменена." });
      await loadInvitations(filter);
    } catch (err) {
      reportError("users.revoke");
      setRevokeError(apiMessage(err, GENERIC));
    } finally {
      setRevokeBusy(false);
    }
  };

  const resend = async (invitation) => {
    setRowBusyId(invitation.id);
    setNotice(null);
    if (!resendKeys.current[invitation.id]) {
      resendKeys.current[invitation.id] = newRequestId();
    }
    try {
      const r = await api.post(`/organization/invitations/${invitation.id}/resend`, {
        request_id: resendKeys.current[invitation.id],
      });
      // The key is spent: the next deliberate click is a NEW action and must
      // rotate the token again rather than replay this one.
      delete resendKeys.current[invitation.id];
      const delivery = r.data?.delivery || {};
      setNotice({
        tone: delivery.delivered === false && !delivery.skipped ? "warn" : "ok",
        text: delivery.message || "Поканата е изпратена отново.",
      });
      await loadInvitations(filter);
    } catch (err) {
      reportError("users.resend");
      const code = apiCode(err);
      if (code === "invitation_resend_cooldown") {
        setNotice({ tone: "warn", text: cooldownText(retryAfterSeconds(err)) });
      } else {
        delete resendKeys.current[invitation.id];
        setNotice({ tone: "warn", text: apiMessage(err, GENERIC) });
      }
      await loadInvitations(filter);
    } finally {
      setRowBusyId("");
    }
  };

  // A revoked or expired invitation is NEVER resurrected. "Създай нова покана"
  // opens the ordinary create flow with the safe profile fields prefilled, so it
  // becomes a new row, a new token, a new request id and a fresh seat check.
  const recreate = (invitation) => {
    openInvite({
      first_name: invitation.first_name || "",
      last_name: invitation.last_name || "",
      email: invitation.invited_email || "",
      phone: invitation.phone || "",
      role: invitation.target_role || "",
    });
  };

  // -------------------------------------------------------------------------
  // Phase 2D.3A — team management (Owner only; the server enforces it again)
  // -------------------------------------------------------------------------
  const runTeamAction = useCallback(
    async (action, successText) => {
      setTeamBusy(true);
      setTeamDialogError("");
      try {
        await action();
        setTeamDialog(null);
        setTeamConfirm(null);
        setNotice({ tone: "ok", text: successText });
        await loadTeams();
        return true;
      } catch (err) {
        reportError("users.teams.action");
        const message = apiMessage(err, GENERIC);
        setTeamDialogError(message);
        if (!teamDialog) setNotice({ tone: "warn", text: message });
        return false;
      } finally {
        setTeamBusy(false);
      }
    },
    [loadTeams, teamDialog],
  );

  const openTeamDialog = (mode, team = null) => {
    setTeamDialogError("");
    setTeamDialog({ mode, team });
  };

  const submitTeamForm = (values) => {
    if (teamDialog?.mode === "create") {
      return runTeamAction(
        () => api.post("/organization/teams", values),
        "Екипът е създаден.",
      );
    }
    return runTeamAction(
      () => api.patch(`/organization/teams/${teamDialog.team.id}`, { name: values.name }),
      "Името на екипа е променено.",
    );
  };

  const submitTeamMember = (values) => {
    const team = teamDialog?.team;
    if (teamDialog?.mode === "move") {
      // Source-centric: the clicked team is where the person is TODAY. The
      // backend moves a person INTO the team in the path, so the DESTINATION
      // chosen in the dialog is the path and the source is the optimistic guard.
      const { path, body } = moveRequest(values);
      return runTeamAction(() => api.post(path, body), "Търговецът е преместен.");
    }
    return runTeamAction(
      () => api.post(`/organization/teams/${team.id}/members`, { member_id: values.member_id }),
      "Търговецът е добавен в екипа.",
    );
  };

  const submitTeamManager = (values) =>
    runTeamAction(
      () => api.post(`/organization/teams/${teamDialog.team.id}/manager`, values),
      "Мениджърът на екипа е сменен.",
    );

  // Phase 2D.3A.2 — ARCHIVED → ACTIVE. Only what actually changes travels: an
  // omitted name keeps the historical one and an omitted manager means "reuse the
  // historical Мениджър if the server still finds them eligible".
  const submitTeamRestore = (values) =>
    runTeamAction(
      () =>
        api.post(
          `/organization/teams/${teamDialog.team.id}/restore`,
          restoreRequest(teamDialog.team, values),
        ),
      "Екипът е възстановен.",
    );

  const confirmTeamAction = () => {
    if (teamConfirm?.mode === "remove") {
      return runTeamAction(
        () =>
          api.delete(
            `/organization/teams/${teamConfirm.team.id}/members/${teamConfirm.member.member_id}`,
          ),
        "Търговецът е премахнат от екипа.",
      );
    }
    // ARCHIVED → DELETED. Soft on the server: the row and its history survive, the
    // team leaves this organization's screens for good.
    if (teamConfirm?.mode === "delete") {
      return runTeamAction(
        () => api.delete(`/organization/teams/${teamConfirm.team.id}`),
        "Екипът е изтрит.",
      );
    }
    return runTeamAction(
      () => api.post(`/organization/teams/${teamConfirm.team.id}/archive`),
      "Екипът е архивиран.",
    );
  };

  // -------------------------------------------------------------------------
  // Member lifecycle
  // -------------------------------------------------------------------------
  const openLifecycle = useCallback(async (mode, member) => {
    setLifecycle({ mode, member });
    setImpact(null);
    setLifecycleError("");
    setImpactLoading(true);
    try {
      // Phase 2D.2 checkpoint 4 — an ownership transfer asks its OWN read-only
      // endpoint, because the answer is a different question (who ends up with
      // which role and what it costs in seats), not a lifecycle impact.
      if (mode === "ownership") {
        const r = await api.get(
          `/organization/members/${member.member_id}/ownership-transfer-impact`,
        );
        setImpact(r.data || null);
        return;
      }
      const r = await api.get(
        `/organization/members/${member.member_id}/lifecycle-impact`,
      );
      setImpact(
        r.data?.member
          ? {
              ...r.data.member,
              role_change: r.data.role_change || null,
              seats: r.data.seats || {},
            }
          : null,
      );
    } catch (err) {
      reportError("users.lifecycleImpact");
      setLifecycleError(apiMessage(err, GENERIC));
    } finally {
      setImpactLoading(false);
    }
  }, []);

  const closeLifecycle = () => {
    setLifecycle(null);
    setImpact(null);
    setLifecycleError("");
  };

  // Every lifecycle mutation is addressed by the MEMBERSHIP id the roster
  // returned, never by an e-mail address: the row being changed is the membership.
  const runLifecycle = async (action, payload, reporterKey) => {
    setLifecycleBusy(true);
    setLifecycleError("");
    try {
      const r = await api.post(
        `/organization/members/${lifecycle.member.member_id}/${action}`,
        payload,
      );
      closeLifecycle();
      setNotice({ tone: "ok", text: r.data?.message || "Промяната е записана." });
      // Seats moved, so the invitation tab numbers must be refetched too - and so
      // must the TEAM read model, because every lifecycle change moves the team
      // candidate lists the server builds: a Търговец promoted to Мениджър becomes
      // an eligible team manager the same second, a deactivated member stops being
      // a candidate, and a reactivated one becomes one again. Without this the
      // Създаване на екип dialog keeps the candidates fetched when the page was
      // opened and wrongly claims there is no free Мениджър.
      // The hand-over is the ONE exception: it turns the acting person into a
      // Търговец, for whom /organization/teams is refused, and the whole page is
      // about to re-render as forbidden anyway.
      await Promise.all([
        loadMembers(),
        loadInvitations(filter),
        ...(action === OWNERSHIP_ACTION ? [] : [loadTeams()]),
      ]);
    } catch (err) {
      reportError(reporterKey);
      setLifecycleError(apiMessage(err, GENERIC));
    } finally {
      setLifecycleBusy(false);
    }
  };

  const submitDeactivate = (replacementMemberId) =>
    runLifecycle(
      "deactivate",
      replacementMemberId ? { replacement_member_id: replacementMemberId } : {},
      "users.deactivate",
    );

  const submitReactivate = () => runLifecycle("reactivate", {}, "users.reactivate");

  const submitRoleChange = (role) => runLifecycle("change-role", { role }, "users.changeRole");

  // Phase 2D.2 checkpoint 4 — after the hand-over the ACTING person is a
  // Търговец, so the membership/role state of the session is refreshed as well:
  // that is what makes the Owner-only controls disappear immediately instead of
  // waiting for a reload.
  const submitOwnershipTransfer = async () => {
    await runLifecycle(OWNERSHIP_ACTION, {}, "users.transferOwnership");
    await refreshOrgs();
  };

  if (!allowed) {
    return (
      <Layout title="Потребители">
        <div className="qw-card" data-testid="users-forbidden" style={{ maxWidth: 520 }}>
          <h1 className="h1">Няма достъп</h1>
          <p className="lead">{FORBIDDEN}</p>
          <Link className="qw-btn primary" to="/dashboard" data-testid="users-forbidden-back">
            Към таблото
          </Link>
        </div>
      </Layout>
    );
  }

  const actions = roles.length > 0 && (
    <button
      type="button"
      className="qw-btn primary"
      onClick={() => openInvite(null)}
      data-testid="users-add-button"
    >
      Добави потребител
    </button>
  );

  return (
    <Layout title="Потребители" actions={actions}>
      <div data-testid="organization-users-page">
        <h1 className="h1">Потребители</h1>
        <p className="lead">
          Управлявайте потребителите, ролите и поканите във вашата организация.
        </p>

        {notice && (
          <div
            className="qw-card"
            role="status"
            style={
              notice.tone === "warn"
                ? { background: "#fffbeb", borderColor: "#fde68a", color: "#92400e", marginTop: 16 }
                : { background: "#ecfdf5", borderColor: "#a7f3d0", color: "#065f46", marginTop: 16 }
            }
            data-testid="users-notice"
          >
            {notice.text}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <UserSeatSummary seats={seats} />
        </div>

        <div
          className="qw-seg"
          role="group"
          aria-label="Раздели"
          data-testid="users-tabs"
          style={{ marginTop: 20, marginBottom: 12 }}
        >
          <button
            type="button"
            onClick={() => setTab("members")}
            aria-pressed={tab === "members"}
            data-testid="users-tab-members"
          >
            Потребители
          </button>
          <button
            type="button"
            onClick={() => setTab("invitations")}
            aria-pressed={tab === "invitations"}
            data-testid="users-tab-invitations"
          >
            Покани
          </button>
          <button
            type="button"
            onClick={() => setTab("teams")}
            aria-pressed={tab === "teams"}
            data-testid="users-tab-teams"
          >
            Екипи
          </button>
        </div>

        {tab === "members" ? (
          <MemberRoster
            items={members}
            loading={membersLoading}
            error={membersError}
            actorRole={role}
            busyEmail={lifecycle?.member?.email || ""}
            onDeactivate={(member) => openLifecycle("deactivate", member)}
            onReactivate={(member) => openLifecycle("reactivate", member)}
            onChangeRole={(member) => openLifecycle("role", member)}
            onTransferOwnership={(member) => openLifecycle("ownership", member)}
          />
        ) : tab === "teams" ? (
          <TeamsPanel
            teams={teams}
            permissions={teamPermissions}
            loading={teamsLoading}
            error={teamsError}
            busy={teamBusy}
            onCreate={() => openTeamDialog("create")}
            onRename={(team) => openTeamDialog("rename", team)}
            onChangeManager={(team) => openTeamDialog("manager", team)}
            onAdd={(team) => openTeamDialog("add", team)}
            onMove={(team) => openTeamDialog("move", team)}
            onRemove={(team, member) => {
              setTeamDialogError("");
              setTeamConfirm({ mode: "remove", team, member });
            }}
            onArchive={(team) => {
              setTeamDialogError("");
              setTeamConfirm({ mode: "archive", team });
            }}
            onRestore={(team) => openTeamDialog("restore", team)}
            onDelete={(team) => {
              setTeamDialogError("");
              setTeamConfirm({ mode: "delete", team });
            }}
          />
        ) : (
          <InvitationRoster
            items={invitations}
            loading={invitationsLoading}
            error={invitationsError}
            filter={filter}
            onFilter={setFilter}
            busyId={rowBusyId}
            onRevoke={(invitation) => {
              setRevokeError("");
              setRevokeTarget(invitation);
            }}
            onResend={resend}
            onRecreate={recreate}
          />
        )}
      </div>

      <InviteMemberDialog
        open={inviteOpen}
        roles={roles}
        seats={seats}
        submitting={inviteBusy}
        serverError={inviteError}
        fieldErrors={inviteFieldErrors}
        prefill={invitePrefill}
        teams={teams}
        canChooseTeam={teamPermissions?.can_manage_teams === true}
        managedTeam={role === "manager" ? ownTeam(teams) : null}
        onCancel={() => {
          setInviteOpen(false);
          setInvitePrefill(null);
        }}
        onSubmit={submitInvite}
      />

      <TeamFormDialog
        open={teamDialog?.mode === "create" || teamDialog?.mode === "rename"}
        mode={teamDialog?.mode}
        team={teamDialog?.team}
        managers={teamCandidates.managers}
        submitting={teamBusy}
        error={teamDialogError}
        onCancel={() => setTeamDialog(null)}
        onSubmit={submitTeamForm}
      />

      <TeamMemberDialog
        open={teamDialog?.mode === "add" || teamDialog?.mode === "move"}
        mode={teamDialog?.mode}
        team={teamDialog?.team}
        teams={teams}
        eligibleSales={teamCandidates.sales}
        submitting={teamBusy}
        error={teamDialogError}
        onCancel={() => setTeamDialog(null)}
        onSubmit={submitTeamMember}
      />

      <TeamManagerDialog
        open={teamDialog?.mode === "manager"}
        team={teamDialog?.team}
        managers={teamCandidates.managers}
        submitting={teamBusy}
        error={teamDialogError}
        onCancel={() => setTeamDialog(null)}
        onSubmit={submitTeamManager}
      />

      <TeamRestoreDialog
        open={teamDialog?.mode === "restore"}
        team={teamDialog?.team}
        teams={teams}
        managers={teamCandidates.managers}
        submitting={teamBusy}
        error={teamDialogError}
        onCancel={() => setTeamDialog(null)}
        onSubmit={submitTeamRestore}
      />

      <ConfirmDialog
        open={!!teamConfirm}
        title={TEAM_CONFIRM_COPY[teamConfirm?.mode]?.title || ""}
        body={TEAM_CONFIRM_COPY[teamConfirm?.mode]?.body || ""}
        confirmLabel={TEAM_CONFIRM_COPY[teamConfirm?.mode]?.confirm || ""}
        cancelLabel="Затвори"
        submittingLabel="Записване…"
        submitting={teamBusy}
        error={teamDialogError}
        tone="danger"
        testId="team-confirm-dialog"
        onCancel={() => {
          setTeamConfirm(null);
          setTeamDialogError("");
        }}
        onConfirm={confirmTeamAction}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        title="Отмяна на покана"
        body="Поканата ще стане невалидна и запазеното лицензирано място ще бъде освободено."
        confirmLabel="Отмени поканата"
        cancelLabel="Затвори"
        submittingLabel="Отмяна…"
        submitting={revokeBusy}
        error={revokeError}
        tone="danger"
        testId="revoke-invitation-dialog"
        onCancel={() => {
          setRevokeTarget(null);
          setRevokeError("");
        }}
        onConfirm={confirmRevoke}
      />

      <MemberDeactivateDialog
        open={lifecycle?.mode === "deactivate"}
        member={lifecycle?.member}
        impact={impact}
        loading={impactLoading}
        error={lifecycleError}
        members={members}
        submitting={lifecycleBusy}
        onCancel={closeLifecycle}
        onSubmit={submitDeactivate}
      />

      <MemberRoleDialog
        open={lifecycle?.mode === "role"}
        member={lifecycle?.member}
        teams={teams}
        impact={impact}
        loading={impactLoading}
        error={lifecycleError}
        submitting={lifecycleBusy}
        onCancel={closeLifecycle}
        onSubmit={submitRoleChange}
      />

      <MemberOwnershipDialog
        open={lifecycle?.mode === "ownership"}
        member={lifecycle?.member}
        impact={impact}
        loading={impactLoading}
        error={lifecycleError}
        submitting={lifecycleBusy}
        onCancel={closeLifecycle}
        onSubmit={submitOwnershipTransfer}
      />

      <ConfirmDialog
        open={lifecycle?.mode === "reactivate"}
        title="Активиране на потребител"
        body={
          impactLoading
            ? "Проверка на свободните места…"
            : impact && impact.can_reactivate === false
              ? "Активирането не е възможно в момента. Проверете свободните лицензирани места или дали потребителят е активирал профила си."
              : REACTIVATION_SUMMARY
        }
        confirmLabel="Активирай"
        cancelLabel="Затвори"
        submittingLabel="Активиране…"
        submitting={lifecycleBusy}
        error={lifecycleError}
        testId="reactivate-member-dialog"
        onCancel={closeLifecycle}
        onConfirm={submitReactivate}
      />
    </Layout>
  );
}
